import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useIdleTimer } from "react-idle-timer";

import { MAX_BUNDLE_AGE_MS } from "./constants";
import LoadStatusContext from "./LoadStatusContext";

export type LoadStatusProviderProps = {
  /** The content to display in normal operating conditions */
  children: ReactNode;
  /** The content to display to prompt the user to reload the the session */
  renderReloadRequiredOverlay: (renderProps: { isVisible: boolean }) => ReactNode;
  /** The timeout for when the page is considered stale */
  staleTimeoutMs?: number;
  /** The timeout for when the page is considered stale and requires a manual reload */
  manualReloadTimeoutMs?: number;
};

/**
 * Will display an error page if we detect a connection problem for a critical error
 * We use the `window` here as the real "provider" for callbacks so that we can access it
 * when we are loading chunks / async scripts at js-boot-time (not at react-mount-time).
 *
 * Will additionally track if the currently loaded page is stale or not based on STALE_LOAD_MS.
 * If so, we will will perform hard refreshes on navigation.
 *
 * This should generally be as high as possible in the applications main react tree, but before
 * any final global error handling.
 *
 * @see {@link load/hooks!useLoadStatus} for consuming in general application code
 */
const LoadStatusProvider = ({
  children,

  staleTimeoutMs = 12 * 60 * 60 * 1000, // 12 hours
  manualReloadTimeoutMs = staleTimeoutMs * 2,
  renderReloadRequiredOverlay,
}: LoadStatusProviderProps) => {
  const [preventReloadRequiredOverlay, setPreventReloadRequiredOverlay] = useState(false);
  const [isStale, setIsStale] = useState(false);
  const [requiresManualReload, setRequiresManualReload] = useState(false);
  const [forceUnauthenticatedReload, setForceUnauthenticatedReload] = useState(false);

  const forceIsStale = useCallback(() => setIsStale(true), []);

  const needsReload = requiresManualReload || forceUnauthenticatedReload;

  useIdleTimer({
    timeout: staleTimeoutMs,
    onIdle: () => {
      setIsStale(true);
    },
  });

  const onRequiresManualReload = useCallback(() => {
    setRequiresManualReload(true);
    // if we require a manual reload we can assume our currently loaded instance is stale
    setIsStale(true);
  }, []);

  const onForceUnauthenticatedReload = useCallback(() => {
    setForceUnauthenticatedReload(true);
    setRequiresManualReload(true);
    setIsStale(true);
  }, []);

  const onPreventReloadRequiredOverlay = useCallback(() => {
    setPreventReloadRequiredOverlay(true);
  }, []);

  useIdleTimer({
    timeout: manualReloadTimeoutMs,
    onIdle: onRequiresManualReload,
  });

  useEffect(() => {
    // Just in case the user is active just enough, make sure we show the reload required overlay
    // after enough time has passed regardless
    const manualReloadTimeout = setTimeout(onRequiresManualReload, MAX_BUNDLE_AGE_MS);
    return () => {
      clearTimeout(manualReloadTimeout);
    };
    // only start timeouts on mount, because that should be a
    // decent proxy for when the page initially loaded
  }, [onRequiresManualReload]);

  const providerValue = useMemo(
    () => ({
      isStale,
      onRequiresManualReload,
      requiresManualReload,
      forceIsStale,
      preventReloadRequiredOverlay,
      onPreventReloadRequiredOverlay,
      forceUnauthenticatedReload,
      onForceUnauthenticatedReload,
      needsReload,
    }),
    [
      isStale,
      onRequiresManualReload,
      requiresManualReload,
      forceIsStale,
      preventReloadRequiredOverlay,
      onPreventReloadRequiredOverlay,
      forceUnauthenticatedReload,
      onForceUnauthenticatedReload,
      needsReload,
    ],
  );

  return (
    <LoadStatusContext.Provider value={providerValue}>
      {children}
      {renderReloadRequiredOverlay({
        isVisible: requiresManualReload && !preventReloadRequiredOverlay,
      })}
    </LoadStatusContext.Provider>
  );
};

export default LoadStatusProvider;
